// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Hidden from "@insite/mobius/Hidden";
import { Typography } from "@insite/mobius/index";
import getColor from "@insite/mobius/utilities/getColor";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import { MediaQuery } from "@rugby/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    withShippedQty?: boolean;
    withSubTotalHeadline?: boolean;
    numberOfChildren?: number;
    withPriceUom?: boolean;
    rightChildren?: string[];
    leftHeaderTitle?: string;
    extendedStyles?: any;
}

export const headCartLineStyles = {
    container: {
        css: css`
            display: flex;
            background: ${getColor("common.ui3")};
            height: 38px;
            align-items: center;
            padding-left: 12px;
            padding-right: 8px;
        `,
    },
    // XNG-Changes: custom width sizing for order history
    infoRightItem: {
        css: css`
            width: ${(props: any) => {
                if (props.withPriceUom) {
                    if (props.item === "PriceUOM/Unit") {
                        return "20%"
                    }
                    return `${80 / (props.numberOfChildren - 1)}%`
                } 
                if (!props.withPriceUom && props.numberOfChildren) {
                    return `${100 / props.numberOfChildren}%`
                }
                return props.withShippedQty ? "20%" : "25%"
            }};
            display: flex;
            justify-content: center;

            @media print {
                ${(props: any) => props.item === "Reorder" ? "display: none;" : ""}

                width: ${(props: any) => {
                    if (props.withPriceUom) {
                        if (props.item === "PriceUOM/Unit") {
                            return "20%"
                        } 
                            return `${80 / (props.numberOfChildren - 2)}%`
                    } 
                    if (!props.withPriceUom && props.numberOfChildren) {
                        return `${100 / (props.numberOfChildren - 1)}%`
                    } 
                    return props.withShippedQty ? "20%" : "25%"
            }};
            }
        `,
    },
    text: {
        size: 13,
        lineHeight: "16px",
        css: css`
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
            @media print {
                word-break: keep-all;
            }
        `,
    },
    infoContainer: {
        css: css`
            flex: 1 0 50%;
        `,
    },
    infoRightContainer: {
        css: css`
            flex: 0 1 50%;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: space-between;
            display: flex;
            align-items: center;

            ${MediaQuery.lt(1160)} {
                display: none;
            }
        `,
    },
    show: {
        css: css`
            display: block;
        `,
    },

    hide: {
        css: css`
            display: none;
        `,
    },
};

const RugbyCartLinesHeader: FC<OwnProps> = ({
    withShippedQty,
    withSubTotalHeadline,
    numberOfChildren,
    withPriceUom,
    rightChildren,
    leftHeaderTitle,
    extendedStyles,
}) => {
    const styles = mergeToNew(headCartLineStyles, extendedStyles);
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.infoContainer}>
                <Typography {...styles.text}>{translate(leftHeaderTitle || "")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.infoRightContainer}>
                {rightChildren?.map(item => (
                    // XNG-Changes: send new properties for styling
                    <StyledWrapper key={uniqueId()} item={item} {...styles.infoRightItem} withShippedQty={withShippedQty} numberOfChildren={numberOfChildren} withPriceUom={withPriceUom}>
                        <Typography {...styles.text}>{translate(item || "")}</Typography>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

RugbyCartLinesHeader.defaultProps = {
    withShippedQty: false,
    leftHeaderTitle: "Item",
    rightChildren: ["Price UOM/Unit", "Qty", "Price/Unit", "Total"],
};
export default RugbyCartLinesHeader;
